import { z } from 'zod';

// ========== Landing Page Variant Configuration ==========

export const LandingVariantFeatureSchema = z.object({
  title: z.string(),
  description: z.string(),
});

export const LandingVariantTestimonialSchema = z.object({
  quote: z.string(),
  name: z.string(),
  location: z.string(),
  verified: z.boolean().default(true),
});

export const LandingVariantProfileSchema = z.object({
  name: z.string(),
  age: z.number(),
  zodiac: z.string(),
  bio: z.string(),
  image: z.string(), // filename in /assets/images/
});

export const LandingVariantPressSchema = z.object({
  outlet: z.enum(['timeout', 'them']),
  quote: z.string(),
  url: z.string().url(),
});

export const LandingVariantFaqSchema = z.object({
  question: z.string(), // plain text or i18n key like "faq.cost.question"
  answer: z.string(),   // plain text or i18n key
});

export const LandingVariantConfigSchema = z.object({
  posthogEvent: z.string(),
  hero: z.object({
    glassBar: z.string(),
    headline: z.string(),
    subheadline: z.string(),
    pillLeft: z.string(),
    pillRight: z.string(),
    ctaText: z.string(),
    avatarImage: z.string(), // filename in /assets/images/
  }),
  features: z.array(LandingVariantFeatureSchema).length(3),
  testimonials: z.array(LandingVariantTestimonialSchema).min(1).max(6),
  profiles: z.array(LandingVariantProfileSchema).min(1).max(5),
  press: z.array(LandingVariantPressSchema).min(0).max(4),
  faq: z.array(LandingVariantFaqSchema).min(1).max(10),
  howItWorksCompact: z.boolean().default(false),
  appStoreSource: z.string(),
});

export type LandingVariantConfig = z.infer<typeof LandingVariantConfigSchema>;
export type LandingVariantFeature = z.infer<typeof LandingVariantFeatureSchema>;
export type LandingVariantTestimonial = z.infer<typeof LandingVariantTestimonialSchema>;
export type LandingVariantProfile = z.infer<typeof LandingVariantProfileSchema>;
export type LandingVariantPress = z.infer<typeof LandingVariantPressSchema>;
export type LandingVariantFaq = z.infer<typeof LandingVariantFaqSchema>;

// Schema for creating/updating a landing variant via API
export const CreateLandingVariantSchema = z.object({
  slug: z.string().min(1).max(20).regex(/^[a-z0-9]+$/, 'Slug must contain only lowercase alphanumeric characters'),
  name: z.string().min(1).max(100),
  language: z.string().min(2).max(5).default('en'),
  isActive: z.boolean().default(true),
  config: LandingVariantConfigSchema,
});

export const UpdateLandingVariantSchema = CreateLandingVariantSchema.partial();

export type CreateLandingVariant = z.infer<typeof CreateLandingVariantSchema>;
export type UpdateLandingVariant = z.infer<typeof UpdateLandingVariantSchema>;

// API response type
export interface LandingVariant {
  id: string;
  slug: string;
  name: string;
  language: string;
  isActive: boolean;
  config: LandingVariantConfig;
  createdAt: string;
  updatedAt: string;
}
